/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { Ref, useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { isBeforeDataFetch, isPhone, useSelectCommon } from '../PRIVATE_SelectCommon';
import { isControlOrFunctionKey, KEYS } from '../utils/PRIVATE_keyboardUtils';
import { DataState, SelectionDetail } from '../UNSAFE_Collection';
import { useId } from '#hooks/UNSAFE_useId';
import { useUser } from '../hooks/UNSAFE_useUser';
import { Item } from '../utils/UNSAFE_dataProvider';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { merge } from '../utils/UNSAFE_stringUtils';
import { Handle } from '../utils/UNSAFE_typeUtils';
import { ValueUpdateDetail } from '../utils/UNSAFE_valueUpdateDetail';
import { SelectMultiple } from './SelectMultiple';
import { SelectedValuesCount } from './SelectedValuesCount';
import { TextTagList } from './TextTagList';
import { useSelectedOnlyData } from './useSelectedOnlyData';
import { isSearchTextEmptyOrUndefined } from '#PRIVATE_SelectCommon';

type TextTagListHandle = Handle<typeof TextTagList, 'ref'>;
type PickedPropsFromSelectMultiple = Pick<
  ComponentProps<typeof SelectMultiple>,
  | 'addToList'
  | 'aria-describedby'
  | 'isDisabled'
  | 'isReadonly'
  | 'onAddToListAction'
  | 'onFilter'
  | 'onLoadRange'
>;
type RenamedPropsFromSelectMultiple = Omit<PickedPropsFromSelectMultiple, 'aria-describedby'> & {
  ariaDescribedBy: PickedPropsFromSelectMultiple['aria-describedby'];
};
export type UseSelectMultipleProps<K, D> = RenamedPropsFromSelectMultiple & {
  data?: DataState<K, D> | null;
  inputRef: Ref<HTMLElement>;
  isFocused?: boolean;
  onCommit: (detail: ValueUpdateDetail<Set<K>>) => void;
  valueItems?: Item<K, D>[];
};

type SelectedValuesCountToggleType = ComponentProps<typeof SelectedValuesCount>['onToggle'];
type SelectedValuesCountToggleDetailType = Parameters<
  Exclude<SelectedValuesCountToggleType, undefined>
>[0];

/**
 * Hook that manages SelectMultiple state and behavior.  This hook creates state variables and
 * event listeners, returning properties to apply to components internally rendered by
 * SelectMultiple, as well as state information.
 *
 * @param ariaDescribedBy Specifies the ID of elements that describe the input.
 * @param data Specifies data for the dropdown list.
 * @param inputRef Ref to the input element (this can be an input in desktop and a div in mobile).
 * @param isDisabled Specifies whether the component is disabled.
 * @param isFocused Specifies whether the component has focus.
 * @param isReadonly Specifies whether the component is readonly.
 * @param onCommit Callback invoked when the selected values are committed.
 * @param onFilter Callback function to trigger loading data for the dropdown list,
 * which may or may not be filtered by user entered text.
 * @param onLoadRange Callback function to handle when the viewport of the dropdown list has
 * changed, including the case where the user scrolls to the end of the list and there are more
 * items to load.  The function should set a new DataState on the component for the specified
 * range.
 * @param valueItems Specifies the keys, data, and optional metadata for the selected values.
 *
 * @returns Properties to apply to internal components that SelectMultiple renders, and component
 * state.
 */
export function useSelectMultiple<K, D>({
  addToList,
  ariaDescribedBy,
  data: propData,
  inputRef,
  isDisabled,
  isFocused: propIsFocused,
  isReadonly,
  onAddToListAction,
  onCommit,
  onFilter,
  onLoadRange: propOnLoadRange,
  valueItems
}: UseSelectMultipleProps<K, D>) {
  const [isDropdownSelectedOnlyView, setDropdownSelectedOnlyView] = useState(false);

  const selectedValuesCount = valueItems?.length;
  const hasSelectedValuesCount =
    (selectedValuesCount !== undefined && selectedValuesCount > 0) || isDropdownSelectedOnlyView;

  // show only the selected values in the dropdown if isDropdownSelectedOnlyView is true,
  // otherwise show the full data set
  // TODO: We may need to revisit this for hierarchical data because the selected valueItems
  // won't have any information about ancestor group nodes, and we should probably show selected
  // items in context within the hierarchy.  Also, the order of the items in the selected-only
  // view right now follows the order in which they were selected, not the order in which they
  // would appear in the full data set.  This may be more of a problem for hierarchical data
  // if items have to appear within the correct group ancestors.  We may need to move
  // construction of the selected-only DataState out of the component to the consuming code,
  // and take a callback that we can call as a notification that the app should provide this
  // special DataState to us (kind of like onLoadRange).  Note that we may also need an
  // enhancement on the DataProvider API to be able to specify a filterCriterion to filter by
  // the selected keys through a fetchFirst call, because a regular fetchByKeys call would
  // still return results in the order in which the keys were specified and would not return
  // data for the ancestor group nodes.
  const selectedOnlyDataProps = useSelectedOnlyData<K, D>({
    isDropdownSelectedOnlyView,
    valueItems
  });
  const data = isDropdownSelectedOnlyView ? selectedOnlyDataProps.data : propData;
  const onLoadRange = isDropdownSelectedOnlyView
    ? selectedOnlyDataProps.onLoadRange
    : propOnLoadRange;
  const handleOnFilter = useCallback<NonNullable<typeof onFilter>>(
    (args) => {
      // when the user is filtering, we need to toggle out from selected only view
      // we consider it filtering only if there is a non-empty filter text is present
      if (!isSearchTextEmptyOrUndefined(args.searchText)) {
        setDropdownSelectedOnlyView(false);
      }
      onFilter?.(args);
    },
    [onFilter]
  ); // setDropdownSelectedOnlyView is stable so not needed in dep array

  const {
    currentRow,
    dropdownRef,
    handleDropdownArrowClick,
    handleDropdownAutoDismiss: baseHandleDropdownAutoDismiss,
    handleDropdownPosition,
    handleInput,
    handleMainFieldKeyDown: baseHandleMainFieldKeyDown,
    handleMainFieldKeyUp,
    handleUpDownArrowKeys,
    isDropdownAbove,
    isDropdownOpen,
    isFocused,
    isUserFiltering,
    mainFieldRef,
    onCurrentKeyChange,
    onMouseDown,
    searchText,
    setCurrentRow,
    setDropdownOpen,
    setUserInput,
    stopFiltering,
    userInput
  } = useSelectCommon<K, D>({
    data,
    inputRef,
    isFocused: propIsFocused,
    onFilter: handleOnFilter
  });

  const [prevIsDropdownOpen, setPrevIsDropdownOpen] = useState(isDropdownOpen);
  const [prevValueItems, setPrevValueItems] = useState(valueItems);

  const [selectedKeys, setSelectedKeys] = useState<Set<K> | undefined>(
    createKeysFromValueItems<K, D>(valueItems)
  );

  const isDataFetched = data !== undefined && data !== null && !isBeforeDataFetch<K, D>(data);
  const isEmptyResults = isDataFetched && data?.totalSize === 0;
  const isAddToListShown =
    addToList === 'on' &&
    isUserFiltering &&
    searchText != null &&
    searchText.length > 0 &&
    isEmptyResults;

  const handleAddToListAction = useCallback(() => {
    setDropdownOpen(false);
    stopFiltering();
    onAddToListAction?.({ searchText: isUserFiltering ? searchText : undefined });
  }, [isUserFiltering, onAddToListAction, searchText, setDropdownOpen, stopFiltering]);

  const textTagListRef = useRef<TextTagListHandle>(null);
  const { direction } = useUser();
  const selectedValuesDescriptionId = useId();
  const selectedValuesKeyboardNavDescriptionId = useId();
  const combinedAriaDescribedBy = merge([
    // when focused, we will show the text tag list.
    // so, we need the screen readers to read them when focusing on the input.
    isFocused && selectedValuesKeyboardNavDescriptionId,
    isFocused && selectedValuesDescriptionId,
    ariaDescribedBy
  ]);

  const removeDropdown = useCallback(() => {
    dropdownRef.current?.parentElement?.removeChild(dropdownRef.current);
  }, [dropdownRef]);

  const shouldSelectCurrentRow = useCallback(
    (currentRowArg: typeof currentRow): currentRowArg is Required<typeof currentRow> => {
      // current row needs to be selected if all the conditions below are satisfied
      // 1. user is filtering
      // 2. dropdown is open
      // 3. user has entered a search text
      // 4. current data state has data
      // 5. currentRow exists

      return (
        isUserFiltering &&
        isDropdownOpen &&
        !isSearchTextEmptyOrUndefined(userInput) &&
        (data?.data.length ?? 0) > 0 &&
        currentRowArg.rowKey !== undefined
      );
    },
    [data, isDropdownOpen, isUserFiltering, userInput]
  );

  const selectRow = useCallback(
    (key: K) => {
      // If the key is already selected, do nothing
      if (selectedKeys?.has(key)) return;
      // If it is not already selected, add it to the selection
      const newValue =
        selectedKeys === undefined ? new Set([key]) : new Set([...selectedKeys.values(), key]);
      onCommit({ previousValue: selectedKeys, value: newValue });
      setSelectedKeys(newValue);
    },
    [onCommit, selectedKeys, setSelectedKeys]
  );

  const myHandleDropdownAutoDismiss = useCallback(
    (event?: Event) => {
      if (isDropdownOpen) {
        if (event?.type === 'keydown') {
          const keyboardEvent = event as KeyboardEvent;
          switch (keyboardEvent.code) {
            case KEYS.TAB:
              // Put focus on an element in the main field, but don't kill the event, so that
              // the browser can naturally transfer focus to the next focusable item relative
              // to the element in the main field.
              if (keyboardEvent.shiftKey || !hasSelectedValuesCount) {
                inputRef.current?.focus();
              } else {
                chipRef.current?.focus();
              }

              // Remove the dropdown immediately, without waiting for a rerender based on changing
              // state, so that the open dropdown cannot get focus when the browser handles the
              // Tab key.
              removeDropdown();
              break;
            default:
              break;
          }
        }
      }
    },
    [hasSelectedValuesCount, inputRef, isDropdownOpen, removeDropdown]
  );

  const myHandleMainFieldKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // const target = event.target as HTMLElement;
      // ignore control key and function key
      if (isControlOrFunctionKey(event)) {
        return;
      }

      switch (event.code) {
        case KEYS.TAB:
          if (isDropdownOpen && (event.shiftKey || (!hasSelectedValuesCount && !event.shiftKey))) {
            // Remove the dropdown immediately, without waiting for a rerender based on changing
            // state, so that the open dropdown cannot get focus when the browser handles the
            // Tab key.
            removeDropdown();

            setDropdownOpen(false);
          }

          // JET-53183 - core pack compat: stop propagation of handled events
          // we don't want a parent collection component to jump focus to the next cell and skip
          // over the selected values count chip
          if (hasSelectedValuesCount && !event.shiftKey) {
            event.stopPropagation();
          }
          break;

        case KEYS.LEFT:
          // Move focus to the text tag if we are in LTR and the cursor is at the
          // beginning of the text (and there is no selection)
          if (
            direction === 'ltr' &&
            inputRef.current instanceof HTMLInputElement &&
            inputRef.current?.selectionStart === 0 &&
            inputRef.current?.selectionEnd === 0
          ) {
            textTagListRef.current?.focus();
          }
          break;

        case KEYS.RIGHT:
          // Move focus to the text tag if we are in RTL and the cursor is at the
          // beginning of the text (and there is no selection)
          if (
            direction === 'rtl' &&
            inputRef.current instanceof HTMLInputElement &&
            inputRef.current?.selectionStart === 0 &&
            inputRef.current?.selectionEnd === 0
          ) {
            textTagListRef.current?.focus();
          }
          break;

        case KEYS.ENTER:
        case KEYS.NUMPAD_ENTER:
          // if this is on a mobile device (only phone and not a tablet device), do not do anything
          if (isPhone()) {
            return;
          }

          // if add to list is shown, there are no results, so trigger the add to list link
          if (isAddToListShown) {
            handleAddToListAction();
            return;
          }

          // if there is a currentRow in the dropdown (meaning a highlighted item that would
          // have keyboard focus if the dropdown list had physical focus, which may be different
          // from the currently selected item), select it and commit it;
          if (shouldSelectCurrentRow(currentRow)) {
            setDropdownOpen(false);
            // after selecting a value, the user is no longer actively filtering, so update related
            // state accordingly so that the newly selected value appears in the field
            stopFiltering();
            selectRow(currentRow.rowKey);
          }
          break;

        default:
          break;
      }
    },
    [
      currentRow,
      direction,
      handleAddToListAction,
      hasSelectedValuesCount,
      inputRef,
      isAddToListShown,
      isDropdownOpen,
      removeDropdown,
      selectRow,
      setDropdownOpen,
      shouldSelectCurrentRow,
      stopFiltering
    ]
  );

  // when the array of valueItems changes, pull out the new set of selectedKeys
  // (don't need to set selectedKeys here on first render because the state was initialized with
  // the correct value)
  if (prevValueItems !== valueItems) {
    const keys = createKeysFromValueItems<K, D>(valueItems);
    setSelectedKeys(keys);
  }

  const chipRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    chipRef.current =
      hasSelectedValuesCount && mainFieldRef.current?.querySelector
        ? mainFieldRef.current.querySelector('[role="switch"]')
        : null;
  }, [hasSelectedValuesCount, isDisabled, isReadonly, mainFieldRef]);

  const handleSelectedValuesCountKeyDown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.code) {
        case KEYS.TAB:
          if (isDropdownOpen && !event.shiftKey) {
            // Remove the dropdown immediately, without waiting for a rerender based on changing
            // state, so that the open dropdown cannot get focus when the browser handles the
            // Tab key.
            removeDropdown();

            setDropdownOpen(false);
          }

          // JET-53183 - core pack compat: stop propagation of handled events
          // we don't want a parent collection component to jump focus to the next cell and skip
          // over the input
          if (event.shiftKey) {
            event.stopPropagation();
          }
          break;

        case KEYS.UP:
        case KEYS.DOWN:
          // prevent the page from scrolling
          event.preventDefault();
          break;

        case KEYS.ESC:
          if (isDropdownOpen) {
            setDropdownOpen(false);

            event.preventDefault();

            // JET-53183 - core pack compat: stop propagation of handled events
            // we don't want a parent collection component to stop editing the row when we close
            // the dropdown
            event.stopPropagation();
          }
          break;

        case KEYS.ENTER:
          // JET-53183 - core pack compat: stop propagation of handled events
          // we don't want a parent collection component to move focus to the next row when
          // we toggle between the full and selected only list in the dropdown
          event.stopPropagation();
          break;

        default:
          break;
      }
    },
    [isDropdownOpen, removeDropdown, setDropdownOpen]
  );

  const handleSelectedValuesCountKeyUp = useCallback(
    (event: KeyboardEvent) => {
      switch (event.code) {
        case KEYS.UP:
        case KEYS.DOWN:
          handleUpDownArrowKeys(event);
          break;

        default:
          break;
      }
    },
    [handleUpDownArrowKeys]
  );

  const handleSelectedValuesCountMouseDown = useCallback(
    (event: MouseEvent) => {
      // call preventDefault so that the dropdown doesn't open showing the full list on mousedown
      // on the chip before switching to show only the selected values on mouseup when the click
      // is handled
      event.preventDefault();
      // explicitly focus the input because it won't happen naturally due to preventDefault()
      inputRef.current?.focus();
    },
    [inputRef]
  );

  const handleSelectedValuesCountToggle = useCallback(
    (detail: SelectedValuesCountToggleDetailType) => {
      if (!isDropdownOpen) {
        setDropdownOpen(true);
      }

      const value = detail.value ?? false;
      if (value !== isDropdownSelectedOnlyView) {
        setDropdownSelectedOnlyView(value);

        // if the selected only toggle is enabled, we need to stop user filtering
        if (value) {
          stopFiltering();
        }
      }
    },
    [isDropdownOpen, isDropdownSelectedOnlyView, setDropdownOpen, stopFiltering]
  );

  // when the dropdown closes, automatically turn off isDropdownSelectedOnlyView
  if (!isDropdownOpen && isDropdownSelectedOnlyView) {
    setDropdownSelectedOnlyView(false);
  }

  const onSelectionChange = useCallback(
    (detail: SelectionDetail<K>) => {
      const newKeys = new Set(detail.value.keys?.values());
      // update the selectedKeys so that the selection visual state of the collection
      // updates immediately while waiting for new valueItems to be set
      setSelectedKeys(newKeys);

      // clear the filter text typed by the user, but do NOT clear the actual filter criterion
      // or highlighting in the list
      setUserInput('');

      // call the onCommit callback so that the consuming code can update the valueItems
      onCommit?.({ value: newKeys, previousValue: selectedKeys });
    },
    [onCommit, selectedKeys, setSelectedKeys, setUserInput]
  );

  const onTextTagListExitNavigation = useCallback(() => {
    // Focus the input on the next tick. Doing it in the same event cycle will
    // trigger the key press on the input element which makes the caret move by
    // one character.
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [inputRef]);

  const onTextTagListRemove = useCallback(
    (keys: K[]) => {
      const previousValue = selectedKeys;
      const value = new Set(selectedKeys);
      keys.forEach((key) => value.delete(key));
      onCommit?.({ previousValue, value });
    },
    [onCommit, selectedKeys]
  );

  const dropdownEventHandlers = useMemo(() => {
    return mergeProps(
      {
        onAutoDismiss: baseHandleDropdownAutoDismiss,
        onPosition: handleDropdownPosition
      },
      { onAutoDismiss: myHandleDropdownAutoDismiss }
    );
  }, [baseHandleDropdownAutoDismiss, handleDropdownPosition, myHandleDropdownAutoDismiss]);

  const inputEventHandlers = useMemo(() => {
    return mergeProps(
      {
        onInput: handleInput,
        onKeyDown: baseHandleMainFieldKeyDown,
        onKeyUp: handleMainFieldKeyUp
      },
      { onKeyDown: myHandleMainFieldKeyDown }
    );
  }, [baseHandleMainFieldKeyDown, handleInput, handleMainFieldKeyUp, myHandleMainFieldKeyDown]);

  const textTagListProps = useMemo(
    () => ({
      textTagListRef,
      onExitNavigation: onTextTagListExitNavigation,
      onRemove: onTextTagListRemove
    }),
    [onTextTagListExitNavigation, onTextTagListRemove]
  );

  // clear the current item when the dropdown opens
  if (isDropdownOpen && !prevIsDropdownOpen) {
    setCurrentRow({ rowKey: undefined });
  }

  // when the dropdown closes, if the user input is empty, reset filtering
  const isDropdownClosing = prevIsDropdownOpen && !isDropdownOpen;
  if (isDropdownClosing && !userInput) {
    stopFiltering();
  }

  // We need to highlight the first result whenever the data changes
  // We cannot use state for the prevData and update it during the render as it is
  // returned by a useMemo hook.
  // There is a bug in preact where if a return value of a useMemo is used for
  // updating a state during the render, it creates an infinite render cycle.
  // See https://github.com/preactjs/preact/issues/4422.
  // Till this issue is fixed, we need to use a ref for this use case.
  const prevData = useRef(data);
  if (prevData.current !== data) {
    // when filtering, set the first result of the filtered data set to be the current item
    // (check the data offset so we don't try to highlight again if the user scrolls through
    // the list and more data is fetched)
    const firstResultKey = data?.data[0]?.metadata.key;
    if (isDropdownOpen && isUserFiltering && firstResultKey != null && data?.offset === 0) {
      // if there are results and search text, highlight the first row, otherwise clear
      // an existing highlight
      setCurrentRow({
        rowKey: isSearchTextEmptyOrUndefined(searchText) ? undefined : firstResultKey
      });
    }

    prevData.current = data;
  }

  if (prevIsDropdownOpen !== isDropdownOpen) {
    setPrevIsDropdownOpen(isDropdownOpen);
  }
  if (prevValueItems !== valueItems) {
    setPrevValueItems(valueItems);
  }

  return {
    addToListEventHandlers: {
      onAction: handleAddToListAction
    },
    ariaProps: {
      ariaDescribedBy: combinedAriaDescribedBy
    },
    collectionProps: {
      currentKey: currentRow.rowKey,
      data,
      onCurrentKeyChange,
      onSelectionChange,
      onLoadRange,
      selectedKeys
    },
    data,
    dropdownArrowEventHandlers: {
      onClick: handleDropdownArrowClick
    },
    dropdownEventHandlers,
    dropdownRef,
    hasSelectedValuesCount,
    inputEventHandlers,
    inputRef,
    isAddToListShown,
    isDataFetched,
    isDropdownAbove,
    isDropdownOpen,
    isDropdownSelectedOnlyView,
    isEmptyResults,
    isFocused,
    isUserFiltering,
    mainFieldRef,
    mouseProps: isDisabled || isReadonly ? {} : { onMouseDown },
    searchText,
    selectedValuesCountProps: {
      onKeyDown: handleSelectedValuesCountKeyDown,
      onKeyUp: handleSelectedValuesCountKeyUp,
      onMouseDown: handleSelectedValuesCountMouseDown,
      onToggle: handleSelectedValuesCountToggle
    },
    selectedValuesDescriptionId,
    selectedValuesKeyboardNavDescriptionId,
    setDropdownOpen,
    setUserInput,
    stopFiltering,
    textTagListProps,
    userInput
  };
}

function createKeysFromValueItems<K, D>(valueItems?: Item<K, D>[]) {
  const keys = valueItems?.reduce((accumKeys, currentItem) => {
    accumKeys.add(currentItem.key);
    return accumKeys;
  }, new Set<K>());
  return keys;
}
